/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import withSignals from '@ion/applications/src/components/hoc/withSignals';
import {selectedScheduledReport, setDeletingScheduledReport} from '@ion/applications/src/pages/analytics/store';
import {translate} from '@ion/core/src/locale/locale';
import {DeletePrompt} from '@ion/dls/src/editors/DeletePrompt';
import {useShowDeletePrompt} from '@ion/dls/src/hooks/useShowDeletePrompt';
import {DeleteResultPrompt} from '@ion/features/src/prompts/DeleteResultPrompt';
import useScheduledReportMutation from '@ion/query/src/hooks/useScheduledReportMutation';

// defines the ScheduledReportsDeletePrompt Prop type definitions ...
export interface ScheduledReportsDeletePromptProps {
  onRefresh?: () => void;
  deletingItem?: any;
};

/**
 * Renders a confirmation prompt for item deletion and manages the deletion process.
 *
 * This component:
 * - Displays a `DeletePrompt` to confirm deletion.
 * - Calls the deletion API and manages success/error states.
 * - Shows a `DeleteResultPrompt` to indicate the outcome.
 * - Calls `onRefresh()` after a successful deletion.
 *
 * ```typescript
 * <ScheduledReportsDeletePrompt onRefresh={refetchItems}
 *   deletingItem={{id: '1234', itemName: 'Muthu's Item'}} />
 * ```
 *
 * @param onRefresh    - The functor to call when the items is deleted.
 * @param deletingItem - The item to be deleted.
 */
export const ScheduledReportsDeletePrompt = ({
  onRefresh,
  deletingItem
}: ScheduledReportsDeletePromptProps) => {
  const {
    setShowError,
    setShowSuccess,
    showSuccess,
    showError,
    failureMessage,
    setFailureMessage,
    closeSnack
  } = useShowDeletePrompt();

  const {
    save,
    isLoading: loading,
    ...rest
  } = useScheduledReportMutation('delete', () => {
    setDeletingScheduledReport(null);
    setShowSuccess(true);
    setShowError(false);
    onRefresh?.();
  }, (response) => {
    setShowSuccess(false);
    setFailureMessage(response?.error?.response?.data?.message);
    setShowError(true);
  });

  const onDeleteConfirm = useCallback(() => {
    save(selectedScheduledReport?.value?.id as any);
  }, [save]);

  const getDeleteMessage = function() {
    return translate('sisense:analytics.deleteConfirm');
  };

  const onDeleteCancel = useCallback(() => {
    setDeletingScheduledReport(null);
  }, []);

  return (
    <>
      <DeletePrompt entity={'Scheduled Report'}
        items={deletingItem}
        isLoading={loading}
        overrideMessage={getDeleteMessage}
        onConfirm={onDeleteConfirm}
        onCancel={onDeleteCancel}
        show={!!deletingItem} />
      <DeleteResultPrompt entityTitle={'Scheduled Report'}
        error={failureMessage}
        isError={showError}
        isSuccess={showSuccess}
        onAction={closeSnack}
        showAsResponse />
    </>
  );
};

export default withSignals(ScheduledReportsDeletePrompt);
